import { BaseQueryFn, createApi, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/dist/query/react';
import {
  cleanIdFromTranscoderProfile,
  TranscoderVideoProfile,
  TranscoderVideoProfileResponse,
  TranscoderVideoProfilesQueryResult,
} from '../../models/transcoder';
import { fetchQueryWithToken } from '../auth.helpers';
import { getSettings, SettingsState } from '../settings.helpers';

const dynamicBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions,
) => {
  const settings = getSettings(api.getState() as SettingsState);
  const urlEnd = typeof args === 'string' ? args : args.url;
  const adjustedUrl = `${settings?.servers.serverTranscoderConfigurator ?? ''}/api/v1/${urlEnd}`;
  const adjustedArgs = typeof args === 'string' ? adjustedUrl : { ...args, url: adjustedUrl };
  return fetchQueryWithToken(adjustedArgs, api, extraOptions);
};

export const transcoderVideoProfileApi = createApi({
  reducerPath: 'transcoderVideoProfileApi',
  baseQuery: dynamicBaseQuery,
  tagTypes: ['VideoProfile'],
  endpoints: builder => ({
    getTranscoderVideoProfiles: builder.query<TranscoderVideoProfile[], void>({
      query: () => 'video-profile',
      providesTags: ['VideoProfile'],
      transformResponse: (response: TranscoderVideoProfile[]) => {
        for (const res of response) {
          if (res.width && res.height) {
            res.resolution = `${res.width}x${res.height}`;
          }
        }
        return response ?? [];
      },
    }),
    createTranscoderVideoProfile: builder.mutation<TranscoderVideoProfilesQueryResult, TranscoderVideoProfile>({
      query: videoProfile => ({
        url: 'video-profile',
        method: 'POST',
        body: cleanIdFromTranscoderProfile(videoProfile),
      }),
      invalidatesTags: ['VideoProfile'],
    }),
    updateTranscoderVideoProfile: builder.mutation<TranscoderVideoProfile, TranscoderVideoProfile>({
      query: videoProfile => ({
        url: 'video-profile',
        method: 'PATCH',
        body: videoProfile,
      }),
      invalidatesTags: ['VideoProfile'],
    }),
    deleteTranscoderVideoProfile: builder.mutation<TranscoderVideoProfile, string>({
      query: id => ({
        url: `video-profile/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['VideoProfile'],
    }),
    getTranscoderVideoProfileByName: builder.query<TranscoderVideoProfileResponse, string>({
      query: name => `video-profile/search/${name}`,
    }),
  }),
});

export const {
  useGetTranscoderVideoProfilesQuery,
  useGetTranscoderVideoProfileByNameQuery,
  useCreateTranscoderVideoProfileMutation,
  useUpdateTranscoderVideoProfileMutation,
  useDeleteTranscoderVideoProfileMutation,
} = transcoderVideoProfileApi;
